/**
 * SOVRANOVA — Hypnosis Hub
 * Four lanes: Guided · Frequency-Enhanced · Sleep · Tone & Affirmations
 * Each session is AI-personalised from memory via HypnosisAgent.
 * Flow: mode → topic → length → player.
 */
import { useState } from 'react'
import { GuidedHypnosisPlayer } from './GuidedHypnosisPlayer'

const TOPICS = [
  { id: 'inner_peace',    emoji: '🕊️', name: 'Inner Peace',         tag: 'Anxiety · Calm · Breath' },
  { id: 'confidence',     emoji: '⚡', name: 'Deep Confidence',      tag: 'Self-worth · Identity' },
  { id: 'inner_child',    emoji: '🤍', name: 'Inner Child',          tag: 'Childhood · Self-compassion' },
  { id: 'self_love',      emoji: '🌸', name: 'Self-Love',            tag: 'Acceptance · Compassion' },
  { id: 'letting_go',     emoji: '🍃', name: 'Letting Go',           tag: 'Forgiveness · Release' },
  { id: 'abundance',      emoji: '✨', name: 'Abundance Mindset',    tag: 'Prosperity · Trust' },
  { id: 'anxiety',        emoji: '🫁', name: 'Soothing Anxiety',     tag: 'Safety · Ground' },
  { id: 'trauma_release', emoji: '💜', name: 'Safe & Grounded',      tag: 'Steadiness · Safety' },
  { id: 'sleep',          emoji: '🌙', name: 'Deep Sleep',           tag: 'Rest · Surrender' },
  { id: 'rewire',         emoji: '🔮', name: 'Tone & Affirmations',   tag: 'Soft tone · Calm focus' },
]

const LENGTHS = [
  { min: 5,  emoji: '⏳', name: '5 minutes',  tag: 'A short reset' },
  { min: 10, emoji: '⏳', name: '10 minutes', tag: 'A deeper settle' },
  { min: 15, emoji: '⏳', name: '15 minutes', tag: 'A full session' },
  { min: 20, emoji: '⏳', name: '20 minutes', tag: 'A long, deep session' },
]

const MODES = [
  {
    id: 'guided',
    emoji: '🎙️',
    name: 'Guided Session',
    desc: 'Rachel\'s warm voice guides you through a full induction, deepening, and gentle guided suggestion. Eyes closed. No headphones needed.',
    duration: 'Choose length',
    color: '#7a6fcf',
  },
  {
    id: 'frequency',
    emoji: '🔊',
    name: 'Frequency-Enhanced',
    desc: 'Guided voice with a soft background tone playing underneath the words. Headphones recommended.',
    duration: 'Choose length',
    color: '#c9a84c',
    headphones: true,
  },
  {
    id: 'sleep',
    emoji: '🌙',
    name: 'Sleep Session',
    desc: 'A spoken induction eases you down, then a soft ambient tone continues quietly. Set your device down and drift off.',
    duration: 'Voice + soft tone',
    color: '#4a7a9b',
  },
  {
    id: 'subconscious',
    emoji: '🔮',
    name: 'Tone & Affirmations',
    desc: 'A soft background tone with a slowly scrolling affirmation script. No voice — best used awake in deep relaxation, letting your eyes rest on each phrase.',
    duration: 'Tone + affirmations',
    color: '#8b5cf6',
    headphones: true,
  },
]

export function HypnosisHub({ onClose, onHome }) {
  const [step,     setStep]     = useState('mode')
  const [mode,     setMode]     = useState(null)
  const [topic,    setTopic]    = useState(null)
  const [minutes,  setMinutes]  = useState(5)
  const [safetyOpen, setSafetyOpen] = useState(() => {
    try { return localStorage.getItem('sov_hyp_safety_v1') !== '1' } catch { return true }
  })
  const dismissSafety = () => { setSafetyOpen(false); try { localStorage.setItem('sov_hyp_safety_v1', '1') } catch {} }

  if (step === 'player') {
    return (
      <GuidedHypnosisPlayer
        topic={topic}
        mode={mode}
        minutes={minutes}
        onClose={() => setStep('length')}
        onHome={onHome || onClose}
        onComplete={() => { setStep('mode'); setMode(null); setTopic(null); setMinutes(5) }}
      />
    )
  }

  const goBack =
    step === 'length' ? () => setStep('topic')
    : step === 'topic' ? () => setStep('mode')
    : onClose

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={goBack}>
          &#8592; Back
        </button>
        <p style={s.headerTitle}>
          {step === 'mode' ? 'Guided Sessions' : `${MODES.find(m => m.id === mode)?.name}`}
        </p>
        <button style={s.homeBtn} onClick={onHome || onClose} title="Dashboard">⌂ Home</button>
      </div>

      {step === 'mode' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>Choose your session</h1>
            <p style={s.subtitle}>Every session is written for you — your name, your chosen focus and your preferences.</p>
          </div>

          {safetyOpen ? (
            <div style={s.safetyCard}>
              <p style={s.safetyTitle}>Before you begin</p>
              <ul style={s.safetyList}>
                <li style={s.safetyItem}>Settle somewhere you can rest — please don't listen while driving or operating machinery. These sessions are designed to relax you and can make you drowsy.</li>
                <li style={s.safetyItem}>You're always in control — pause, stop, or open your eyes anytime. If relaxing ever leaves you feeling more restless than calm (it happens to some people), just stop and take a break.</li>
                <li style={s.safetyItem}>This is a wellness and relaxation tool — not therapy or medical treatment, and not a substitute for professional care.</li>
                <li style={s.safetyItem}>Please check with a health professional first if you're pregnant, have epilepsy or are sensitive to pulsing sounds (choose a voice-only Guided session), or live with psychosis, a dissociative condition, or significant trauma. If you're in crisis, use Urgent help or your local emergency services.</li>
              </ul>
              <button style={s.safetyAck} onClick={dismissSafety}>I understand</button>
            </div>
          ) : (
            <button style={s.safetyReopen} onClick={() => setSafetyOpen(true)}>&#9432; Safety &amp; how to listen</button>
          )}

          <div style={s.modeGrid}>
            {MODES.map(m => (
              <button key={m.id} style={s.modeCard} onClick={() => { setMode(m.id); setStep('topic') }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={s.modeEmoji}>{m.emoji}</span>
                  <span style={s.modeDuration}>{m.duration}</span>
                </div>
                <h3 style={s.modeName}>{m.name}</h3>
                <p style={s.modeDesc}>{m.desc}</p>
                {m.headphones && (
                  <p style={s.headphoneTag}>🎧 Headphones recommended</p>
                )}
                <div style={{ ...s.modeBar, background: m.color }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'topic' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>What do you need today?</h1>
            <p style={s.subtitle}>Your session will be written around the focus you choose.</p>
          </div>

          <div style={s.topicGrid}>
            {TOPICS.map(t => (
              <button key={t.id} style={s.topicCard}
                onClick={() => { setTopic(t.id); setStep('length') }}>
                <span style={s.topicEmoji}>{t.emoji}</span>
                <div>
                  <p style={s.topicName}>{t.name}</p>
                  <p style={s.topicTag}>{t.tag}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'length' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>How long?</h1>
            <p style={s.subtitle}>Choose a session length. You can stop any time.</p>
          </div>

          <div style={s.topicGrid}>
            {LENGTHS.map(l => (
              <button key={l.min} style={s.topicCard}
                onClick={() => { setMinutes(l.min); setStep('player') }}>
                <span style={s.topicEmoji}>{l.emoji}</span>
                <div>
                  <p style={s.topicName}>{l.name}</p>
                  <p style={s.topicTag}>{l.tag}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Organic restyle (Board 4 "the deep room", 2026-08-13): warm ink replaces the
// cold purple; Caprasimo titles; pill topic rows. Safety card keeps its amber
// "read me" tone and full copy. Values only — flow and behavior untouched.
const s = {
  page:        { display:'flex', flexDirection:'column', height:'100%', background:'linear-gradient(170deg,#2e2b25,#211e19)', overflowY:'auto' },
  header:      { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.25rem', borderBottom:'1px solid rgba(245,234,216,.08)', background:'rgba(46,43,37,.95)', flexShrink:0, position:'sticky', top:0, zIndex:10 },
  backBtn:     { background:'transparent', border:'none', color:'rgba(245,234,216,.45)', fontSize:'0.875rem', cursor:'pointer', width:60, textAlign:'left' },
  homeBtn:     { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.7)', borderRadius:'999px', padding:'0.35rem 0.8rem', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 },
  headerTitle: { fontSize:'0.9rem', fontWeight:600, color:'#f5ead8', letterSpacing:'0.05em' },
  content:     { flex:1, padding:'1.5rem 1.25rem 2rem', display:'flex', flexDirection:'column', gap:'1.5rem' },
  intro:       { textAlign:'center' },
  title:       { fontFamily:'var(--og-fh, Georgia, serif)', fontWeight:400, fontSize:'1.55rem', color:'#f5ead8', marginBottom:'0.5rem' },
  subtitle:    { fontSize:'0.875rem', color:'rgba(245,234,216,.5)', lineHeight:1.6 },
  modeGrid:    { display:'flex', flexDirection:'column', gap:'0.875rem' },
  modeCard:    { background:'rgba(245,234,216,.05)', border:'none', borderRadius:'22px', padding:'1.25rem', textAlign:'left', cursor:'pointer', position:'relative', overflow:'hidden', transition:'all .2s' },
  modeEmoji:   { fontSize:'1.6rem', display:'block', marginBottom:'0.5rem' },
  modeDuration:{ fontSize:'0.68rem', color:'rgba(245,234,216,.4)', letterSpacing:'0.06em', background:'rgba(245,234,216,.07)', padding:'0.2rem 0.6rem', borderRadius:'999px' },
  modeName:    { fontFamily:'var(--og-fh, Georgia, serif)', fontWeight:400, fontSize:'1.1rem', color:'#f5ead8', marginBottom:'0.4rem' },
  modeDesc:    { fontSize:'0.825rem', color:'rgba(245,234,216,.55)', lineHeight:1.6, marginBottom:'0.5rem' },
  headphoneTag:{ fontSize:'0.72rem', color:'#c9a84c', marginTop:'0.25rem' },
  modeBar:     { position:'absolute', bottom:0, left:0, right:0, height:3, opacity:0.75 },
  topicGrid:   { display:'flex', flexDirection:'column', gap:'0.625rem' },
  topicCard:   { display:'flex', alignItems:'center', gap:'1rem', background:'rgba(245,234,216,.05)', border:'none', borderRadius:'999px', padding:'0.8rem 1.15rem', textAlign:'left', cursor:'pointer', transition:'all .2s' },
  topicEmoji:  { fontSize:'1.4rem', flexShrink:0 },
  topicName:   { fontSize:'0.92rem', fontWeight:600, color:'#f5ead8', marginBottom:'0.1rem' },
  topicTag:    { fontSize:'0.74rem', color:'rgba(245,234,216,.42)' },
  safetyCard:  { background:'rgba(201,168,76,.09)', border:'1px solid rgba(201,168,76,.3)', borderRadius:'20px', padding:'1rem 1.15rem' },
  safetyTitle: { fontSize:'0.78rem', fontWeight:700, color:'#c9a84c', letterSpacing:'0.05em', marginBottom:'0.5rem' },
  safetyList:  { listStyle:'disc', paddingLeft:'1.1rem', margin:0, display:'flex', flexDirection:'column', gap:'0.45rem' },
  safetyItem:  { fontSize:'0.78rem', color:'rgba(245,234,216,.65)', lineHeight:1.55 },
  safetyAck:   { marginTop:'0.85rem', background:'rgba(245,234,216,.1)', border:'none', color:'#f5ead8', borderRadius:'999px', padding:'0.5rem 1.2rem', fontSize:'0.8rem', fontWeight:600, cursor:'pointer' },
  safetyReopen:{ alignSelf:'flex-start', background:'transparent', border:'none', color:'rgba(201,168,76,.75)', fontSize:'0.76rem', cursor:'pointer', padding:0 },
}
